import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { DollarSign, TrendingUp, Repeat, Package, Plus, Loader2, Trash2, CreditCard, Receipt, Pencil, Wrench, Boxes } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ServicesCatalog from "./ServicesCatalog";
import InventoryTab from "./InventoryTab";

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-6 rounded-tenant border-border animate-fade-up">
      <div className="flex items-start justify-between mb-5">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-tenant" strokeWidth={1.5} />
      </div>
      <div className="font-display font-semibold tracking-tight text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

// Mapa tab -> seção. Cada seção tem: header, ícone, subtítulo, abas.
const SERVICE_TABS = ["services", "packages", "plans"];
const INVENTORY_TABS = ["inventory"];
const FIN_TABS = ["payments", "subscriptions"];
const ALL_TABS = [...SERVICE_TABS, ...INVENTORY_TABS, ...FIN_TABS];

function sectionOf(tab) {
  if (SERVICE_TABS.includes(tab)) return "services";
  if (INVENTORY_TABS.includes(tab)) return "inventory";
  if (FIN_TABS.includes(tab)) return "financeiro";
  return "financeiro";
}

const SECTION_META = {
  services: {
    icon: Wrench,
    kicker: "Serviços",
    title: "Catálogo & recorrência",
    subtitle: "Serviços que você oferece, pacotes prepagos e planos mensais.",
    defaultTab: "services",
    tabs: [
      { value: "services", label: "Lista de Serviços", testid: "fin-tab-services" },
      { value: "packages", label: "Pacotes", testid: "fin-tab-packages" },
      { value: "plans", label: "Planos", testid: "fin-tab-plans" },
    ],
  },
  inventory: {
    icon: Boxes,
    kicker: "Estoque",
    title: "Produtos & fornecedores",
    subtitle: "Controle de entrada, saída e reposição do seu estoque.",
    defaultTab: "inventory",
    tabs: [
      { value: "inventory", label: "Estoque", testid: "fin-tab-inventory" },
    ],
    hideTabs: true, // InventoryTab já tem seu próprio sub-nav (Painel/Produtos/Fornecedores)
  },
  financeiro: {
    icon: DollarSign,
    kicker: "Financeiro",
    title: "Receita & recorrência",
    subtitle: "Histórico de pagamentos e assinaturas mensais.",
    defaultTab: "payments",
    tabs: [
      { value: "payments", label: "Histórico de Pagamentos", testid: "fin-tab-payments" },
      { value: "subscriptions", label: "Assinaturas / Mensalidades", testid: "fin-tab-subscriptions" },
    ],
  },
};

export default function TenantFinanceiro() {
  const [stats, setStats] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const activeTab = ALL_TABS.includes(urlTab) ? urlTab : "services";
  const section = sectionOf(activeTab);
  const meta = SECTION_META[section];
  const HeaderIcon = meta.icon;

  useEffect(() => { api.get("/tenant/financeiro/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const reloadStats = () => {
    api.get("/tenant/financeiro/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  const showStats = section === "financeiro";

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid={`fin-section-${section}`}>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
          <HeaderIcon className="w-3.5 h-3.5" /> {meta.kicker}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{meta.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{meta.subtitle}</p>
      </div>

      {showStats && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Tile icon={DollarSign} label="Faturamento confirmado" value={`R$ ${(stats.revenue_confirmed_month ?? stats.revenue_month ?? 0).toLocaleString("pt-BR", {minimumFractionDigits: 2})}`} sub={`${stats.confirmed_count_month || 0} recebimentos no mês`} />
          <Tile icon={Wrench} label="Receita prevista" value={`R$ ${(stats.revenue_pending || 0).toLocaleString("pt-BR", {minimumFractionDigits: 2})}`} sub={`${stats.pending_count || 0} pendente(s) de confirmação`} />
          <Tile icon={Repeat} label="Receita mensal recorrente" value={`R$ ${stats.mrr.toLocaleString("pt-BR", {minimumFractionDigits: 2})}`} sub={`${stats.active_subscriptions} assinaturas ativas`} />
          <Tile icon={Package} label="Pacotes vendidos" value={stats.active_client_packages} sub="ativos no momento" />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", v);
        setSearchParams(next, { replace: true });
      }}>
        {!meta.hideTabs && (
          <TabsList>
            {meta.tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={t.testid}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        )}
        <TabsContent value="services" className="mt-6"><ServicesCatalog /></TabsContent>
        <TabsContent value="inventory" className="mt-6"><InventoryTab /></TabsContent>
        <TabsContent value="packages" className="mt-6"><Packages /></TabsContent>
        <TabsContent value="plans" className="mt-6"><Plans /></TabsContent>
        <TabsContent value="subscriptions" className="mt-6"><Subscriptions /></TabsContent>
        <TabsContent value="payments" className="mt-6"><Payments onChanged={reloadStats} /></TabsContent>
      </Tabs>
    </div>
  );
}

const BILLING_INTERVAL_OPTIONS = [
  { value: "weekly", label: "Semanal", suffix: "/sem" },
  { value: "monthly", label: "Mensal", suffix: "/mês" },
  { value: "quarterly", label: "Trimestral", suffix: "/trim" },
  { value: "semi_annual", label: "Semestral", suffix: "/sem" },
  { value: "annual", label: "Anual", suffix: "/ano" },
];
const billingLabel = (v) => (BILLING_INTERVAL_OPTIONS.find((o) => o.value === v) || BILLING_INTERVAL_OPTIONS[1]).label;
const billingSuffix = (v) => (BILLING_INTERVAL_OPTIONS.find((o) => o.value === v) || BILLING_INTERVAL_OPTIONS[1]).suffix;

const emptyPackageForm = { name: "", service_id: "", sessions: 5, price: 200, validity_days: 365, benefits: "", suggested_interval_days: "" };

function Packages() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(emptyPackageForm);
  const [editing, setEditing] = useState(null); // package being edited (or null for create)
  const [open, setOpen] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Pedidos do portal (item 1)
  const [portalRequests, setPortalRequests] = useState([]);
  const [requestsBusy, setRequestsBusy] = useState(null); // id da ação em curso

  const loadPortalRequests = async () => {
    try {
      const r = await api.get("/tenant/clients/package-requests");
      setPortalRequests(r.data.items || []);
    } catch (_e) { /* silencioso — bloco é opcional */ }
  };

  const load = async () => {
    const r = await api.get("/tenant/financeiro/packages");
    setItems(r.data.items || []);
    const s = await api.get("/tenant/services");
    setServices(s.data.items || []);
    await loadPortalRequests();
  };
  useEffect(() => { load(); }, []);

  const approveRequest = async (req) => {
    setRequestsBusy(req.id);
    try {
      await api.post(`/tenant/clients/package-requests/${req.id}/approve`);
      toast.success(`Pedido de ${req.client_name} aprovado`);
      await loadPortalRequests();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setRequestsBusy(null); }
  };
  const rejectRequest = async (req) => {
    if (!window.confirm(`Recusar o pedido de ${req.client_name} (${req.package_name})?`)) return;
    setRequestsBusy(req.id);
    try {
      await api.post(`/tenant/clients/package-requests/${req.id}/reject`);
      toast.success("Pedido recusado");
      await loadPortalRequests();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setRequestsBusy(null); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyPackageForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      service_id: p.service_id || "",
      sessions: p.sessions,
      price: p.price,
      validity_days: p.validity_days,
      benefits: (p.benefits || []).join("\n"),
      suggested_interval_days: p.suggested_interval_days ?? "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      sessions: Number(form.sessions),
      price: Number(form.price),
      validity_days: Number(form.validity_days),
      benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      suggested_interval_days: form.suggested_interval_days === "" || form.suggested_interval_days == null
        ? null
        : Number(form.suggested_interval_days),
    };
    try {
      if (editing) {
        await api.put(`/tenant/financeiro/packages/${editing.id}`, payload);
        toast.success("Pacote atualizado");
      } else {
        await api.post("/tenant/financeiro/packages", payload);
        toast.success("Pacote criado");
      }
      setOpen(false); setEditing(null); load();
    } catch (er) {
      toast.error(formatApiError(er));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tenant/financeiro/packages/${confirmDelete.id}`);
      toast.success("Pacote excluído");
      setConfirmDelete(null);
      load();
    } catch (er) {
      toast.error(formatApiError(er));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="pkg-add" className="gap-2" onClick={openCreate}
          style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
          <Plus className="w-4 h-4" /> Novo pacote
        </Button>
      </div>

      {portalRequests.length > 0 && (
        <section
          data-testid="package-portal-requests"
          className="rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-sm">Pedidos do portal</div>
              <div className="text-xs text-muted-foreground">
                {portalRequests.length} pedido(s) de pacote aguardando aprovação do cliente.
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {portalRequests.map((r) => (
              <div
                key={r.id}
                data-testid={`package-portal-request-${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-card border border-border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {r.client_name} <span className="text-muted-foreground">·</span>{" "}
                    {r.package_name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.code} · {r.created_at ? new Date(r.created_at).toLocaleString("pt-BR") : "—"} ·{" "}
                    R$ {Number(r.price || 0).toFixed(2)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={requestsBusy === r.id}
                    data-testid={`package-portal-reject-${r.id}`}
                    onClick={() => rejectRequest(r)}
                  >
                    Recusar
                  </Button>
                  <Button
                    size="sm"
                    disabled={requestsBusy === r.id}
                    data-testid={`package-portal-approve-${r.id}`}
                    onClick={() => approveRequest(r)}
                    style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
                  >
                    Aprovar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent data-testid="pkg-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar pacote" : "Novo pacote"}</DialogTitle>
            <DialogDescription>Ex: &quot;5 cortes + 10% off&quot;. Descreva o que o cliente recebe.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label>Nome</Label>
              <Input required data-testid="pkg-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5"><Label>Serviço principal</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="pkg-form-service"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Sessões</Label>
                <Input required type="number" min={1} data-testid="pkg-form-sessions" value={form.sessions} onChange={(e) => setForm({ ...form, sessions: e.target.value })} />
              </div>
              <div className="space-y-1.5"><Label>Preço (R$)</Label>
                <Input required type="number" step="0.01" data-testid="pkg-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div className="space-y-1.5"><Label>Validade (dias)</Label>
                <Input required type="number" data-testid="pkg-form-validity" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Intervalo sugerido entre sessões (dias)</Label>
              <Input
                type="number"
                min={1}
                placeholder="Ex: 15"
                data-testid="pkg-form-interval"
                value={form.suggested_interval_days}
                onChange={(e) => setForm({ ...form, suggested_interval_days: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Se o cliente tem saldo mas não vem há mais que o dobro deste intervalo,
                o sistema alerta na Visão Geral (Sentinela de Pacotes). Deixe em branco para desativar.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Discriminação (o que abrange)</Label>
              <textarea
                className="w-full text-sm rounded-md border border-border bg-card px-3 py-2"
                rows={4}
                data-testid="pkg-form-benefits"
                placeholder="Uma linha por item. Ex:&#10;5 fotos tratadas em alta resolução&#10;Escolha do fundo&#10;Retoque de pele"
                value={form.benefits}
                onChange={(e) => setForm({ ...form, benefits: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Uma linha por item. Vai aparecer na landing pública e no card.</p>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!form.service_id} data-testid="pkg-form-submit">
                {editing ? "Salvar alterações" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-tenant">Sem pacotes ainda.</Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <Card key={p.id} className="p-5 rounded-tenant border-border flex flex-col" data-testid={`pkg-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <Badge variant="outline" className="max-w-full truncate">{p.service_name}</Badge>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`pkg-edit-${p.id}`} title="Editar">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(p)} data-testid={`pkg-delete-${p.id}`} title="Excluir" className="text-destructive hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="font-display font-semibold text-lg break-words">{p.name}</div>
              <div className="text-tenant font-display font-bold text-2xl mt-2">R$ {p.price.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground mt-1">{p.sessions} sessões · {p.validity_days} dias</div>
              {(p.benefits || []).length > 0 && (
                <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                  {p.benefits.map((b, i) => <li key={i}>• {b}</li>)}
                </ul>
              )}
              <div className="mt-4 pt-3 border-t border-border">
                <Button size="sm" variant="outline" className="w-full" onClick={() => setPurchase(p)} data-testid={`pkg-sell-${p.id}`}>
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Vender
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {purchase && <PurchaseDialog pkg={purchase} onClose={() => setPurchase(null)} onDone={() => { setPurchase(null); toast.success("Pacote vendido!"); }} />}

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Excluir pacote</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o pacote <b>{confirmDelete?.name}</b>?
              Vendas já registradas continuam válidas para os clientes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="pkg-confirm-delete">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PurchaseDialog({ pkg, onClose, onDone }) {
  const [clients, setClients] = useState([]); const [client_id, setCid] = useState("");
  useEffect(() => { api.get("/tenant/clients", { params: { limit: 500 } }).then(({ data }) => setClients(data.items || [])); }, []);
  const buy = async () => { try { await api.post("/tenant/financeiro/packages/purchase", { client_id, package_id: pkg.id, paid: true }); onDone(); } catch (er) { toast.error(formatApiError(er)); } };
  return (
    <Dialog open={!!pkg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Vender {pkg.name}</DialogTitle><DialogDescription>R$ {pkg.price.toFixed(2)} · {pkg.sessions} sessões</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Cliente</Label>
            <Select value={client_id} onValueChange={setCid}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button onClick={buy} disabled={!client_id} className="w-full gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}><CreditCard className="w-4 h-4" /> Registrar venda</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const emptyPlanForm = { name: "", monthly_price: 99, benefits: "", billing_interval: "monthly", suggested_interval_days: "" };

function Plans() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyPlanForm);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const r = await api.get("/tenant/financeiro/plans");
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      monthly_price: p.monthly_price,
      benefits: (p.benefits || []).join("\n"),
      billing_interval: p.billing_interval || "monthly",
      suggested_interval_days: p.suggested_interval_days ?? "",
    });
  };

  const cancelEdit = () => { setEditing(null); setForm(emptyPlanForm); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      monthly_price: Number(form.monthly_price),
      benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      suggested_interval_days: form.suggested_interval_days === "" || form.suggested_interval_days == null
        ? null
        : Number(form.suggested_interval_days),
    };
    try {
      if (editing) {
        await api.put(`/tenant/financeiro/plans/${editing.id}`, payload);
        toast.success("Plano atualizado");
      } else {
        await api.post("/tenant/financeiro/plans", payload);
        toast.success("Plano criado");
      }
      setEditing(null);
      setForm(emptyPlanForm);
      load();
    } catch (er) {
      toast.error(formatApiError(er));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tenant/financeiro/plans/${confirmDelete.id}`);
      toast.success("Plano excluído");
      setConfirmDelete(null);
      load();
    } catch (er) {
      toast.error(formatApiError(er));
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 rounded-tenant border-border h-fit" data-testid="plan-form-card">
        <div className="font-display font-semibold mb-3">
          {editing ? `Editar plano: ${editing.name}` : "Novo plano"}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome</Label>
            <Input required placeholder="Ex: VIP Mensal" data-testid="plan-form-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Preço (R$)</Label>
              <Input required type="number" step="0.01" data-testid="plan-form-price"
                value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cobrança</Label>
              <Select value={form.billing_interval} onValueChange={(v) => setForm({ ...form, billing_interval: v })}>
                <SelectTrigger data-testid="plan-form-interval"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BILLING_INTERVAL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`plan-form-interval-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Intervalo sugerido entre usos (dias)</Label>
            <Input
              type="number" min={1} placeholder="Ex: 15"
              data-testid="plan-form-usage-interval"
              value={form.suggested_interval_days}
              onChange={(e) => setForm({ ...form, suggested_interval_days: e.target.value })}
            />
            <p className="text-[11px] text-muted-foreground">
              Se o cliente assina mas não vem há mais que o dobro deste intervalo,
              alertamos na Visão Geral (Sentinela de Uso). Deixe em branco para desativar.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Benefícios (1 por linha)</Label>
            <textarea className="w-full text-sm rounded-md border border-border bg-card px-3 py-2" rows={4}
              placeholder="Ex:&#10;Maquiagem social&#10;Ensaio fotográfico em família&#10;5 fotos editadas"
              data-testid="plan-form-benefits"
              value={form.benefits}
              onChange={(e) => setForm({ ...form, benefits: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" data-testid="plan-form-submit"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              {editing ? "Salvar alterações" : "Criar plano"}
            </Button>
            {editing && (
              <Button type="button" variant="ghost" onClick={cancelEdit} data-testid="plan-form-cancel">
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Card>

      <div className="lg:col-span-2 grid md:grid-cols-2 gap-3">
        {items.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground rounded-tenant md:col-span-2">
            Sem planos ainda.
          </Card>
        ) : (
          items.map((p) => (
            <Card key={p.id} className="p-5 rounded-tenant border-border flex flex-col" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-lg break-words">{p.name}</div>
                  <Badge variant="outline" className="mt-1 text-[10px]">{billingLabel(p.billing_interval)}</Badge>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`} title="Editar">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(p)} data-testid={`plan-delete-${p.id}`} title="Excluir" className="text-destructive hover:text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-tenant font-display font-bold text-2xl mt-2">
                R$ {p.monthly_price.toFixed(2)}
                <span className="text-xs text-muted-foreground font-sans font-normal">{billingSuffix(p.billing_interval)}</span>
              </div>
              {(p.benefits || []).length > 0 && (
                <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                  {p.benefits.map((b, i) => <li key={i}>• {b}</li>)}
                </ul>
              )}
            </Card>
          ))
        )}
      </div>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Excluir plano</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o plano <b>{confirmDelete?.name}</b>?
              Assinaturas ativas continuam válidas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="plan-confirm-delete">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Subscriptions() {
  const [items, setItems] = useState([]); const [plans, setPlans] = useState([]); const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: "", plan_id: "" });
  const load = async () => { const [s, p, c] = await Promise.all([api.get("/tenant/financeiro/subscriptions"), api.get("/tenant/financeiro/plans"), api.get("/tenant/clients", { params: { limit: 500 } })]); setItems(s.data.items || []); setPlans(p.data.items || []); setClients(c.data.items || []); };
  useEffect(() => { load(); }, []);
  const subscribe = async (e) => { e.preventDefault(); try { await api.post("/tenant/financeiro/subscriptions", form); toast.success("Assinatura criada"); setForm({ client_id: "", plan_id: "" }); load(); } catch (er) { toast.error(formatApiError(er)); } };
  const bill = async (id) => { try { await api.post(`/tenant/financeiro/subscriptions/${id}/bill`); toast.success("Cobrança registrada"); load(); } catch (er) { toast.error(formatApiError(er)); } };
  const cancel = async (id) => { if (!window.confirm("Cancelar?")) return; await api.post(`/tenant/financeiro/subscriptions/${id}/cancel`); load(); };
  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-tenant border-border">
        <form onSubmit={subscribe} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px] space-y-1.5"><Label>Cliente</Label><Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex-1 min-w-[200px] space-y-1.5"><Label>Plano</Label><Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · R$ {p.monthly_price.toFixed(2)}</SelectItem>)}</SelectContent></Select></div>
          <Button type="submit" disabled={!form.client_id || !form.plan_id} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}><Plus className="w-4 h-4" /> Assinar</Button>
        </form>
      </Card>
      <Card className="rounded-tenant border-border overflow-hidden">
        {items.length === 0 ? <div className="p-8 text-center text-muted-foreground">Sem assinaturas ainda.</div> :
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground"><tr><th className="text-left px-4 py-2 whitespace-nowrap">Cliente</th><th className="text-left px-4 py-2 whitespace-nowrap">Plano</th><th className="text-right px-4 py-2 whitespace-nowrap">Mensal</th><th className="text-left px-4 py-2 whitespace-nowrap">Status</th><th className="text-left px-4 py-2 whitespace-nowrap">Próxima</th><th className="text-right px-4 py-2 whitespace-nowrap">Ações</th></tr></thead>
            <tbody>{items.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-2 whitespace-nowrap">{s.client_name}</td><td className="px-4 py-2 whitespace-nowrap">{s.plan_name}</td><td className="px-4 py-2 text-right font-mono whitespace-nowrap">R$ {s.monthly_price.toFixed(2)}</td>
                <td className="px-4 py-2 whitespace-nowrap"><Badge variant="outline">{s.status}</Badge></td>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{s.next_billing_at ? new Date(s.next_billing_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">{s.status === "active" && <><Button size="sm" variant="ghost" onClick={() => bill(s.id)}><Receipt className="w-4 h-4" /></Button><Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(s.id)}><Trash2 className="w-4 h-4" /></Button></>}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>}
      </Card>
    </div>
  );
}

function Payments({ onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/tenant/financeiro/payments").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const confirm = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/tenant/financeiro/payments/${p.id}/confirm`);
      toast.success(`Recebimento de R$ ${p.amount.toFixed(2)} confirmado`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e, "Erro ao confirmar")); }
    finally { setBusy(null); }
  };
  const cancelPay = async (p) => {
    if (!window.confirm(`Cancelar cobrança de R$ ${p.amount.toFixed(2)} de ${p.client_name}?`)) return;
    setBusy(p.id);
    try {
      await api.post(`/tenant/financeiro/payments/${p.id}/cancel`);
      toast.success("Cobrança cancelada");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e, "Erro ao cancelar")); }
    finally { setBusy(null); }
  };

  const badge = (status) => {
    if (status === "pending") return <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">Pendente</Badge>;
    if (status === "cancelled") return <Badge variant="outline" className="bg-zinc-500/10 text-zinc-700 border-zinc-500/30">Cancelado</Badge>;
    return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">Confirmado</Badge>;
  };

  return (
    <Card className="rounded-tenant border-border overflow-hidden" data-testid="payments-table">
      {items.length === 0 ? <div className="p-8 text-center text-muted-foreground">Sem pagamentos ainda.</div> :
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 whitespace-nowrap">Data</th>
              <th className="text-left px-4 py-2 whitespace-nowrap">Cliente</th>
              <th className="text-left px-4 py-2 whitespace-nowrap">Tipo</th>
              <th className="text-left px-4 py-2">Descrição</th>
              <th className="text-left px-4 py-2 whitespace-nowrap">Status</th>
              <th className="text-right px-4 py-2 whitespace-nowrap">Valor</th>
              <th className="text-right px-4 py-2 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>{items.map((p) => {
            const status = p.status || "confirmed"; // compat: registros antigos sem status
            const isPending = status === "pending";
            const amountClass = isPending ? "text-amber-700" : status === "cancelled" ? "text-muted-foreground line-through" : "text-emerald-700";
            return (
              <tr key={p.id} className="border-t border-border" data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-2 text-xs whitespace-nowrap">{new Date(p.paid_at).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-2 whitespace-nowrap">{p.client_name}</td>
                <td className="px-4 py-2 whitespace-nowrap"><Badge variant="outline">{p.type}</Badge></td>
                <td className="px-4 py-2">{p.description}</td>
                <td className="px-4 py-2 whitespace-nowrap">{badge(status)}</td>
                <td className={`px-4 py-2 text-right font-mono whitespace-nowrap ${amountClass}`}>
                  {isPending ? "" : "+"}R$ {p.amount.toFixed(2)}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {isPending && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline"
                              disabled={busy === p.id}
                              onClick={() => confirm(p)}
                              data-testid={`payment-confirm-${p.id}`}>
                        {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirmar"}
                      </Button>
                      <Button size="sm" variant="ghost"
                              disabled={busy === p.id}
                              onClick={() => cancelPay(p)}
                              className="text-red-600 hover:text-red-600 hover:bg-red-500/10"
                              data-testid={`payment-cancel-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
        </div>}
    </Card>
  );
}
